/**
 * 운영자 셸 (/admin) — 어두운 사이드바 콘솔. 넓은 화면은 고정 사이드바, 좁은 화면은 상단 바 + 가로 스크롤 메뉴.
 * ★ 본문은 `min-w-0` — 없으면 표의 가로 스크롤이 페이지 전체로 샌다.
 * ★ 본문 컨테이너 클래스(`space-y-4` 등)는 영역 분리 전과 같다 — 화면 본문이 여기에 기댄다.
 */
import type { Me } from '@geo-admin/shared/contract/api/auth.ts';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useLogout } from '../lib/me.ts';
import { ADMIN_NAV } from '../lib/nav.ts';
import { LogoMark } from './LogoMark.tsx';

const itemClass =
  'block shrink-0 whitespace-nowrap rounded-md px-3 py-1.5 text-sm text-slate-400 transition-colors hover:bg-white/5 hover:text-white';

export function AdminShell({ me, children }: { me: Me; children: ReactNode }) {
  const logout = useLogout();

  return (
    <div className="min-h-screen lg:flex">
      <aside className="bg-slate-950 text-slate-300 lg:sticky lg:top-0 lg:flex lg:h-screen lg:w-56 lg:shrink-0 lg:flex-col">
        <div className="flex items-center gap-2 px-4 py-3 lg:py-5">
          <Link to="/admin" aria-label="노출 현황으로">
            <LogoMark tone="onDark" size="sm" />
          </Link>
          <span className="rounded bg-live/25 px-1.5 py-0.5 font-semibold text-[11px] text-blue-200">운영자</span>
          <button
            type="button"
            onClick={() => void logout(me)}
            className="ml-auto text-slate-400 text-xs hover:text-white lg:hidden"
          >
            로그아웃
          </button>
        </div>
        <nav className="flex gap-1 overflow-x-auto px-2 pb-2 lg:block lg:flex-1 lg:space-y-5 lg:overflow-y-auto lg:px-3 lg:pb-4">
          {ADMIN_NAV.map((g) => (
            <div key={g.group ?? 'home'} className="contents lg:block lg:space-y-0.5">
              {g.group ? (
                <p className="hidden px-3 pb-1 font-semibold text-[11px] text-slate-500 tracking-wide lg:block">
                  {g.group}
                </p>
              ) : null}
              {g.items.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  activeOptions={{ exact: n.to === '/admin' }}
                  className={itemClass}
                  activeProps={{ className: 'bg-white/10 text-white' }}
                >
                  {n.label}
                </Link>
              ))}
            </div>
          ))}
        </nav>
        <div className="hidden border-white/10 border-t px-4 py-3 text-xs lg:block">
          <p className="truncate text-slate-400" title={me.email}>
            {me.email}
          </p>
          <div className="mt-2 flex items-center gap-3">
            <Link to="/admin/account/password" className="text-slate-400 hover:text-white">
              비밀번호
            </Link>
            <button type="button" onClick={() => void logout(me)} className="text-slate-400 hover:text-white">
              로그아웃
            </button>
          </div>
        </div>
      </aside>
      <main className="min-w-0 flex-1">
        <div className="mx-auto max-w-7xl space-y-4 px-4 py-6">{children}</div>
      </main>
    </div>
  );
}
